import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import {
  Heart, Compass, Shield, ExternalLink, ChevronLeft, ChevronRight, Sparkles, Home, ArrowRight, Gift, Check
} from 'lucide-react';
import { affirmationCards, decisionCards, fearVsTruthCards, GUMROAD_LINKS } from '../data/cardContent';
import { toast } from 'sonner';

const PDF_FILES = {
  affirmation: { standard: '/cards/affirmation-cards-standard-2.5x3.5.pdf', postcard: '/cards/affirmation-cards-postcard-4x6.pdf' },
  decision: { standard: '/cards/decision-cards-standard-2.5x3.5.pdf', postcard: '/cards/decision-cards-postcard-4x6.pdf' },
  feartruth: { standard: '/cards/fear-vs-truth-cards-standard-2.5x3.5.pdf', postcard: '/cards/fear-vs-truth-cards-postcard-4x6.pdf' },
};

const IMAGE_FOLDERS = {
  affirmation: { standard: 'affirmation-standard', postcard: 'affirmation-postcard', count: 20, prefix: 'affirmation-card' },
  decision: { standard: 'decision-standard', postcard: 'decision-postcard', count: 12, prefix: 'decision-card' },
  feartruth: { standard: 'fear-vs-truth-standard', postcard: 'fear-vs-truth-postcard', count: 15, prefix: 'fear-vs-truth-card' },
};

function openImageGallery(type, size) {
  const info = IMAGE_FOLDERS[type];
  const folder = info[size];
  const prefix = info.prefix;
  const count = info.count;
  const sizeLabel = size === 'standard' ? '2.5" x 3.5"' : '4" x 6"';
  const typeName = type === 'affirmation' ? 'Faith-Based Affirmation' : type === 'decision' ? 'Faith-Led Decision' : 'Fear vs Truth';

  let imagesHTML = `
      <div style="text-align:center;margin-bottom:24px;padding:16px;background:#7B1045;border-radius:12px;">
        <p style="color:#F3E5AB;font-size:14px;font-weight:bold;margin-bottom:8px;">CARD BACK DESIGN</p>
        <img src="/cards/images/${folder}/card-back.png" 
             style="max-width:100%;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.3);"
             alt="Card Back" />
        <p style="margin-top:8px;font-size:12px;color:#F3E5AB;">Long press / right-click to save</p>
      </div>`;
  for (let i = 1; i <= count; i++) {
    const num = String(i).padStart(2, '0');
    imagesHTML += `
      <div style="text-align:center;margin-bottom:20px;">
        <img src="/cards/images/${folder}/${prefix}-${num}.png" 
             style="max-width:100%;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.1);"
             alt="Card ${i}" />
        <p style="margin-top:8px;font-size:13px;color:#6B5B6E;">Card ${i} of ${count} — Long press / right-click to save</p>
      </div>`;
  }

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
    <title>${typeName} Cards - ${sizeLabel}</title>
    <style>
      body { font-family: 'Nunito', sans-serif; background: #FEFCFD; margin: 0; padding: 0; }
      .header { background: #7B1045; color: white; padding: 20px; text-align: center; position: sticky; top: 0; z-index: 10; }
      .header h1 { font-size: 20px; margin: 0 0 4px; }
      .header p { font-size: 13px; opacity: 0.8; margin: 0; }
      .gallery { max-width: 500px; margin: 0 auto; padding: 20px; }
      .tip { background: #FFF9E6; border-left: 4px solid #D4AF37; padding: 12px 16px; margin: 0 20px 20px; border-radius: 0 8px 8px 0; font-size: 13px; color: #2D2030; }
    </style></head><body>
    <div class="header">
      <h1>${typeName} Cards</h1>
      <p>${sizeLabel} · ${count} Cards · 300 DPI Print Quality</p>
    </div>
    <div class="tip"><strong>To save:</strong> On phone, long-press each image → "Save Image". On desktop, right-click → "Save Image As".</div>
    <div class="gallery">${imagesHTML}</div>
  </body></html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

const CARD_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Nunito:wght@400;600;700&display=swap');
  @page { margin: 8mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Nunito', sans-serif; background: #fff; }
  h1,h2,h3,h4 { font-family: 'Cormorant Garamond', serif; }
  .print-grid { display: flex; flex-wrap: wrap; gap: 4mm; justify-content: center; padding: 4mm; }
`;

const BRAND = {
  burgundy: '#7B1045',
  burgundyDark: '#5C0C33',
  gold: '#D4AF37',
  goldLight: '#F3E5AB',
  cream: '#FEFCFD',
  charcoal: '#2D2030',
};

function buildAffirmationCardHTML(card, w, h) {
  return `
    <div style="width:${w};height:${h};border:1px solid #E5D5DA;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;page-break-inside:avoid;background:#fff;">
      <div style="background:linear-gradient(145deg,${BRAND.burgundy},${BRAND.burgundyDark});padding:${h === '3.5in' ? '10px 12px' : '16px 18px'};text-align:center;">
        <p style="color:${BRAND.goldLight};font-size:${h === '3.5in' ? '7pt' : '9pt'};text-transform:uppercase;letter-spacing:3px;margin-bottom:2px;">Faith-Based Affirmation</p>
        <div style="width:30px;height:2px;background:${BRAND.gold};margin:4px auto;"></div>
      </div>
      <div style="flex:1;display:flex;flex-direction:column;justify-content:center;align-items:center;padding:${h === '3.5in' ? '10px' : '18px'};text-align:center;">
        <p style="font-family:'Cormorant Garamond',serif;font-size:${h === '3.5in' ? '10pt' : '13pt'};font-weight:600;color:${BRAND.charcoal};line-height:1.4;margin-bottom:10px;">"${card.affirmation}"</p>
        <div style="width:20px;height:1px;background:${BRAND.gold};margin:6px auto;"></div>
        <p style="font-family:'Cormorant Garamond',serif;font-style:italic;font-size:${h === '3.5in' ? '8pt' : '10pt'};color:${BRAND.burgundy};line-height:1.3;margin-top:6px;">${card.scripture}</p>
        <p style="font-size:${h === '3.5in' ? '7pt' : '8pt'};color:${BRAND.gold};margin-top:4px;font-weight:600;">${card.reference}</p>
      </div>
      <div style="background:${BRAND.goldLight};padding:${h === '3.5in' ? '4px' : '6px'};text-align:center;">
        <p style="font-size:${h === '3.5in' ? '6pt' : '7pt'};color:${BRAND.burgundy};letter-spacing:1px;">BREAKTHROUGH &bull; PURPOSE RECLAIMED COACHING</p>
      </div>
    </div>`;
}

function buildDecisionCardHTML(card, w, h) {
  return `
    <div style="width:${w};height:${h};border:1px solid #E5D5DA;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;page-break-inside:avoid;background:#fff;">
      <div style="background:linear-gradient(145deg,${BRAND.gold},#C49B2F);padding:${h === '3.5in' ? '8px 10px' : '14px 16px'};text-align:center;">
        <p style="color:#fff;font-size:${h === '3.5in' ? '6pt' : '8pt'};text-transform:uppercase;letter-spacing:3px;">Faith-Led Decision</p>
        <p style="color:#fff;font-family:'Cormorant Garamond',serif;font-size:${h === '3.5in' ? '10pt' : '13pt'};font-weight:700;margin-top:2px;">${card.title}</p>
      </div>
      <div style="flex:1;display:flex;flex-direction:column;justify-content:space-between;padding:${h === '3.5in' ? '8px 10px' : '14px 16px'};">
        <div>
          <p style="font-size:${h === '3.5in' ? '8pt' : '10pt'};color:${BRAND.charcoal};font-weight:700;margin-bottom:4px;">Reflect:</p>
          <p style="font-size:${h === '3.5in' ? '8pt' : '10pt'};color:${BRAND.charcoal};font-style:italic;line-height:1.3;">"${card.prompt}"</p>
        </div>
        <div style="background:#FDF5F8;border-radius:6px;padding:${h === '3.5in' ? '6px' : '10px'};margin-top:6px;">
          <p style="font-size:${h === '3.5in' ? '7pt' : '9pt'};color:${BRAND.burgundy};line-height:1.3;">${card.prayer}</p>
        </div>
        <div style="text-align:center;margin-top:4px;">
          <p style="font-family:'Cormorant Garamond',serif;font-style:italic;font-size:${h === '3.5in' ? '7pt' : '9pt'};color:${BRAND.gold};">${card.scripture}</p>
          <p style="font-size:${h === '3.5in' ? '6pt' : '7pt'};color:${BRAND.gold};font-weight:600;">${card.reference}</p>
        </div>
      </div>
      <div style="background:${BRAND.burgundy};padding:${h === '3.5in' ? '3px' : '5px'};text-align:center;">
        <p style="font-size:${h === '3.5in' ? '6pt' : '7pt'};color:${BRAND.goldLight};letter-spacing:1px;">PURPOSE RECLAIMED COACHING</p>
      </div>
    </div>`;
}

function buildFearTruthCardHTML(card, w, h) {
  return `
    <div style="width:${w};height:${h};border:1px solid #E5D5DA;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;page-break-inside:avoid;background:#fff;">
      <div style="background:#2D2030;padding:${h === '3.5in' ? '8px 10px' : '14px 16px'};text-align:center;">
        <p style="color:#E5D5DA;font-size:${h === '3.5in' ? '6pt' : '8pt'};text-transform:uppercase;letter-spacing:3px;">The Lie Fear Tells You</p>
        <p style="color:#fff;font-family:'Cormorant Garamond',serif;font-size:${h === '3.5in' ? '9pt' : '11pt'};font-style:italic;margin-top:4px;line-height:1.3;">"${card.fear}"</p>
      </div>
      <div style="flex:1;display:flex;flex-direction:column;justify-content:center;align-items:center;padding:${h === '3.5in' ? '10px' : '16px'};text-align:center;background:linear-gradient(180deg,#FEFCFD,${BRAND.goldLight}30);">
        <div style="width:30px;height:2px;background:${BRAND.gold};margin-bottom:8px;"></div>
        <p style="font-size:${h === '3.5in' ? '6pt' : '8pt'};text-transform:uppercase;letter-spacing:2px;color:${BRAND.burgundy};margin-bottom:6px;">The Truth God Declares</p>
        <p style="font-family:'Cormorant Garamond',serif;font-size:${h === '3.5in' ? '9pt' : '12pt'};font-weight:600;color:${BRAND.charcoal};line-height:1.4;">"${card.truth}"</p>
        <p style="font-size:${h === '3.5in' ? '7pt' : '9pt'};color:${BRAND.gold};font-weight:600;margin-top:8px;">${card.reference}</p>
      </div>
      <div style="background:${BRAND.burgundy};padding:${h === '3.5in' ? '3px' : '5px'};text-align:center;">
        <p style="font-size:${h === '3.5in' ? '6pt' : '7pt'};color:${BRAND.goldLight};letter-spacing:1px;">BREAKTHROUGH &bull; FEAR VS TRUTH</p>
      </div>
    </div>`;
}

function generateCardPDF(type, size) {
  const isStandard = size === 'standard';
  const w = isStandard ? '2.5in' : '4in';
  const h = isStandard ? '3.5in' : '6in';
  const cols = isStandard ? 3 : 2;

  let cards, title, buildFn;
  if (type === 'affirmation') {
    cards = affirmationCards; title = 'Faith-Based Affirmation Cards'; buildFn = buildAffirmationCardHTML;
  } else if (type === 'decision') {
    cards = decisionCards; title = 'Faith-Led Decision Cards'; buildFn = buildDecisionCardHTML;
  } else {
    cards = fearVsTruthCards; title = 'Fear vs Truth Scripture Cards'; buildFn = buildFearTruthCardHTML;
  }

  const cardsHTML = cards.map(c => buildFn(c, w, h)).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>${CARD_STYLES}
    .print-grid { max-width: ${isStandard ? '8in' : '8.5in'}; margin: 0 auto; }
    @media print { .no-print { display:none; } }
    </style></head><body>
    <div class="no-print" style="text-align:center;padding:20px;background:${BRAND.burgundy};color:#fff;">
      <h1 style="font-family:'Cormorant Garamond',serif;font-size:22pt;">${title}</h1>
      <p style="font-size:10pt;opacity:0.9;margin:6px 0 12px;">Size: ${isStandard ? '2.5" x 3.5" (Standard Playing Card)' : '4" x 6" (Postcard)'} &bull; ${cards.length} Cards</p>
      <p style="font-size:10pt;">Press <strong>Ctrl+P</strong> (or Cmd+P on Mac) to print. Set margins to "Minimum" for best results.</p>
    </div>
    <div class="print-grid">${cardsHTML}</div>
    <div class="no-print" style="text-align:center;padding:20px;">
      <p style="font-size:9pt;color:#999;">Purpose Reclaimed Coaching &bull; Janelle McGowan-Charles</p>
    </div>
    </body></html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    toast.success(`${title} ready to print!`);
  }
}

const CardPreview = ({ card, type }) => {
  if (type === 'affirmation') {
    return (
      <div className="group relative bg-white rounded-xl border border-sage/20 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
        <div className="bg-gradient-to-br from-sage to-sage-dark p-4 text-center">
          <p className="text-gold-light text-[10px] uppercase tracking-[3px]">Faith-Based Affirmation</p>
          <div className="w-8 h-0.5 bg-gold mx-auto mt-2" />
        </div>
        <div className="p-5 text-center">
          <p className="font-heading text-base text-charcoal font-semibold leading-snug mb-3">"{card.affirmation}"</p>
          <div className="w-5 h-px bg-gold mx-auto my-3" />
          <p className="font-heading italic text-sm text-sage leading-snug">{card.scripture}</p>
          <p className="text-xs text-gold font-semibold mt-2">{card.reference}</p>
        </div>
        <div className="bg-gold-light/40 py-1.5 text-center">
          <p className="text-[9px] text-sage tracking-wider">BREAKTHROUGH</p>
        </div>
      </div>
    );
  }

  if (type === 'decision') {
    return (
      <div className="group relative bg-white rounded-xl border border-gold/20 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
        <div className="bg-gradient-to-br from-gold to-gold/80 p-4 text-center">
          <p className="text-white/80 text-[10px] uppercase tracking-[3px]">Faith-Led Decision</p>
          <p className="font-heading text-white text-lg font-bold mt-1">{card.title}</p>
        </div>
        <div className="p-5">
          <p className="text-xs font-bold text-charcoal mb-1">Reflect:</p>
          <p className="text-sm text-charcoal italic leading-snug mb-3">"{card.prompt}"</p>
          <div className="bg-cream-paper rounded-lg p-3 mb-3">
            <p className="text-xs text-sage leading-snug">{card.prayer}</p>
          </div>
          <p className="font-heading italic text-xs text-gold text-center">{card.scripture}</p>
          <p className="text-[10px] text-gold font-semibold text-center mt-1">{card.reference}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative bg-white rounded-xl border border-charcoal/10 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
      <div className="bg-charcoal p-4 text-center">
        <p className="text-stone-400 text-[10px] uppercase tracking-[3px]">The Lie Fear Tells You</p>
        <p className="font-heading text-white italic text-sm mt-2 leading-snug">"{card.fear}"</p>
      </div>
      <div className="p-5 text-center bg-gradient-to-b from-white to-gold-light/10">
        <div className="w-8 h-0.5 bg-gold mx-auto mb-3" />
        <p className="text-[10px] uppercase tracking-[2px] text-sage mb-2">The Truth God Declares</p>
        <p className="font-heading text-base text-charcoal font-semibold leading-snug">"{card.truth}"</p>
        <p className="text-xs text-gold font-semibold mt-3">{card.reference}</p>
      </div>
    </div>
  );
};

const CardSetSection = ({ type, title, subtitle, description, icon: Icon, cards, accentColor, gumroadLink, count, price }) => {
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(cards.length / perPage);
  const visible = cards.slice(page * perPage, page * perPage + perPage);

  return (
    <section data-testid={`card-set-${type}`} className="mb-16">
      <div className="text-center mb-8">
        <div className={`w-16 h-16 rounded-2xl ${accentColor} flex items-center justify-center mx-auto mb-4`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl text-charcoal mb-2">{title}</h2>
        <p className="text-sm text-sage font-semibold uppercase tracking-wider mb-3">{subtitle}</p>
        <p className="text-charcoal-light max-w-xl mx-auto">{description}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {visible.map(card => (
          <CardPreview key={card.id} card={card} type={type} />
        ))}
      </div>

      <div className="flex items-center justify-center gap-3 mb-8">
        <button
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
          className="p-2 rounded-full border border-stone-200 disabled:opacity-30 hover:bg-stone-50 transition-colors"
          data-testid={`${type}-prev-btn`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm text-charcoal-light">
          {page + 1} / {totalPages} &middot; {count} cards total
        </span>
        <button
          onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
          disabled={page === totalPages - 1}
          className="p-2 rounded-full border border-stone-200 disabled:opacity-30 hover:bg-stone-50 transition-colors"
          data-testid={`${type}-next-btn`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
          <span className="font-heading text-2xl text-sage font-bold">{price}</span>
          <a
            href={gumroadLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-sage text-white rounded-full font-bold text-lg hover:bg-sage-dark transition-all shadow-md hover:shadow-lg"
            data-testid={`${type}-purchase-btn`}
          >
            <ExternalLink className="w-5 h-5" /> Purchase Card Set
          </a>
        </div>
        <p className="text-xs text-charcoal-light text-center mt-3">Includes Digital Reading Version + Printable Version</p>
      </div>
    </section>
  );
};

export const CardShop = () => {
  return (
    <div data-testid="card-shop-page" className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero */}
      <div className="relative bg-gradient-to-b from-charcoal to-sage-dark text-white py-16 px-6 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-gold/30 rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-gold/20 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-gold/10 rounded-full" />
        </div>
        <div className="relative max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="text-gold text-sm font-semibold uppercase tracking-[4px]">New Release</span>
            <Sparkles className="w-5 h-5 text-gold" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4">
            Faith-Based Card Collections
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-2">
            Beautifully designed cards to support your healing journey, guide your decisions, and replace fear with God's truth.
          </p>
          <p className="text-sm text-gold/80">
            Available as digital downloads &bull; Print-ready in two sizes &bull; Perfect for personal use, gifts, or vendor stalls
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">

        {/* Quick nav */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <a href="#affirmation" className="px-4 py-2 rounded-full border border-sage/30 text-sage text-sm font-medium hover:bg-sage hover:text-white transition-all" data-testid="nav-affirmation">
            Affirmation Cards (20)
          </a>
          <a href="#decision" className="px-4 py-2 rounded-full border border-gold/30 text-gold text-sm font-medium hover:bg-gold hover:text-white transition-all" data-testid="nav-decision">
            Decision Cards (12)
          </a>
          <a href="#feartruth" className="px-4 py-2 rounded-full border border-charcoal/30 text-charcoal text-sm font-medium hover:bg-charcoal hover:text-white transition-all" data-testid="nav-feartruth">
            Fear vs Truth Cards (15)
          </a>
          <a href="#bundle" className="px-4 py-2 rounded-full border border-sage/30 bg-sage/5 text-sage text-sm font-medium hover:bg-sage hover:text-white transition-all" data-testid="nav-bundle">
            Bundle (All 47)
          </a>
        </div>

        {/* Affirmation Cards */}
        <div id="affirmation">
          <CardSetSection
            type="affirmation"
            title="Faith-Based Affirmation Cards"
            subtitle="20 Cards &bull; Trauma Healing & Breakthrough"
            description="Daily declarations rooted in scripture to rewire the lies trauma planted in your mind. Speak life over yourself, one card at a time."
            icon={Heart}
            cards={affirmationCards}
            count={20}
            accentColor="bg-sage"
            gumroadLink={GUMROAD_LINKS.affirmation}
            price="£15"
          />
        </div>

        {/* Decision Cards */}
        <div id="decision">
          <CardSetSection
            type="decision"
            title="Faith-Led Decision Cards"
            subtitle="12 Cards &bull; Godly Decision-Making Prompts"
            description="When life demands a choice, these cards guide you back to God's voice. Each card offers a reflection prompt, a prayer, and a scripture anchor."
            icon={Compass}
            cards={decisionCards}
            count={12}
            accentColor="bg-gold"
            gumroadLink={GUMROAD_LINKS.decision}
            price="£10"
          />
        </div>

        {/* Fear vs Truth Cards */}
        <div id="feartruth">
          <CardSetSection
            type="feartruth"
            title="Fear vs Truth Scripture Cards"
            subtitle="15 Cards &bull; Confronting Lies With God's Word"
            description="Every card names a fear that trauma survivors carry and counters it with the unshakeable truth of scripture. Keep them in your bag, on your mirror, or by your bedside."
            icon={Shield}
            cards={fearVsTruthCards}
            count={15}
            accentColor="bg-charcoal"
            gumroadLink={GUMROAD_LINKS.fearVsTruth}
            price="£12.50"
          />
        </div>

        {/* Bundle Section */}
        <div id="bundle" data-testid="card-bundle-section" className="mb-16">
          <div className="relative bg-gradient-to-br from-sage-dark via-sage to-sage-dark rounded-3xl overflow-hidden p-8 md:p-12">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-6 right-10 w-40 h-40 border-2 border-gold/40 rounded-full" />
              <div className="absolute bottom-8 left-8 w-24 h-24 border border-gold/30 rounded-full" />
            </div>
            <div className="relative text-center">
              <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                <Gift className="w-3.5 h-3.5" /> Best Value
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-3">
                Complete Card Collection
              </h2>
              <p className="text-white/70 text-lg mb-6 max-w-xl mx-auto">
                All 3 card sets in one beautiful bundle — 47 cards total to guide your healing journey.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <Heart className="w-6 h-6 text-gold mx-auto mb-2" />
                  <p className="text-white font-semibold text-sm">20 Affirmation Cards</p>
                  <p className="text-white/60 text-xs mt-1">Daily breakthrough declarations</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <Compass className="w-6 h-6 text-gold mx-auto mb-2" />
                  <p className="text-white font-semibold text-sm">12 Decision Cards</p>
                  <p className="text-white/60 text-xs mt-1">Faith-led guidance prompts</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <Shield className="w-6 h-6 text-gold mx-auto mb-2" />
                  <p className="text-white font-semibold text-sm">15 Fear vs Truth Cards</p>
                  <p className="text-white/60 text-xs mt-1">Scripture-backed truth</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 text-white/70 text-sm mb-8">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-gold" /> Print-ready PDFs</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-gold" /> 2 sizes included</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-gold" /> Instant download</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-gold" /> Save vs buying separately</span>
              </div>

              {GUMROAD_LINKS.bundle ? (
                <a
                  href={GUMROAD_LINKS.bundle}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-charcoal rounded-full font-bold text-lg hover:bg-gold-light transition-all shadow-lg hover:shadow-xl"
                  data-testid="bundle-purchase-btn"
                >
                  <Gift className="w-5 h-5" /> Get the Complete Bundle
                </a>
              ) : (
                <div className="inline-flex items-center gap-2 px-8 py-4 bg-gold/30 text-white rounded-full font-bold text-lg cursor-default" data-testid="bundle-coming-soon">
                  <Sparkles className="w-5 h-5" /> Bundle Coming Soon
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12 mb-2">
          <p className="text-xs text-charcoal-light mb-4">&copy; 2025 Janelle McGowan-Charles. All rights reserved. Purpose Reclaimed Coaching.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sage hover:text-sage-dark transition-colors font-medium"
            data-testid="back-home-btn"
          >
            <Home className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};
